/**
 * AppInsights adapter.
 *
 * Wraps Azure Application Insights behind the {@link TelemetryAdapter}
 * facade so the rest of the app stays SDK-agnostic.
 *
 * Constructed only when a non-empty connection string is available;
 * Phase 13's wiring in `Main.tsx` guards on that. When unconstructed,
 * `telemetry` from `lib/telemetry` stays a no-op.
 */
import { ApplicationInsights, type IExceptionTelemetry } from '@microsoft/applicationinsights-web';

import type { TelemetryAdapter } from './telemetry';

export const createAppInsightsAdapter = (connectionString: string): TelemetryAdapter => {
  const ai = new ApplicationInsights({
    config: {
      connectionString,
      // We drive trackPageView manually from App.tsx's page-state machine,
      // so disable the SDK's history-based auto-tracking.
      enableAutoRouteTracking: false,
      disableFetchTracking: false,
      enableCorsCorrelation: true,
    },
  });
  ai.loadAppInsights();

  return {
    trackEvent: (name, properties) => {
      ai.trackEvent({ name }, properties);
    },
    trackException: (error, properties) => {
      const payload: IExceptionTelemetry = { exception: error };
      ai.trackException(payload, properties);
    },
    trackPageView: (name, properties) => {
      ai.trackPageView({ name, properties });
    },
  };
};
